import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Stack,
  IconButton,
  Button,
  TextField,
  MenuItem,
  Link,
  Alert,
} from '@mui/material';
import {
  Email,
  Phone,
  CalendarToday,
  Source,
  Edit,
  Save,
  Cancel,
  Download,
  Visibility,
  CloudUpload,
  Delete as DeleteIcon,
  Description,
} from '@mui/icons-material';
import { Candidate as CandidateType } from '../types';

// Extend the base Candidate type with additional fields needed by this component
interface Candidate extends Omit<CandidateType, 'status'> {
  status: string; // Override to allow more status values
  application_source?: string;
  application_date?: string;
  evaluation_status?: string;
  cover_letter?: string;
  resume_file_path?: string;
  cover_letter_file_path?: string;
  pi_factor_a?: number;
  pi_factor_b?: number;
  pi_factor_c?: number;
  pi_factor_d?: number;
  position_match_score?: number;
  match_breakdown?: string | object;
  pi_person_id?: string;
  pi_last_sync_at?: string;
  pi_assessment_completed_at?: string;
}

interface Job {
  id: string;
  title: string;
  status: string;
}

interface CandidateInfoProps {
  candidate: Candidate;
  onUpdate?: (updates: Partial<Candidate>) => Promise<void>;
  onDocumentUpload?: (files: { resume?: File; coverLetter?: File }) => Promise<void>;
  isEditing?: boolean;
  onEditToggle?: () => void;
  jobs?: Job[];
}

const CandidateInfo: React.FC<CandidateInfoProps> = ({
  candidate,
  onUpdate,
  onDocumentUpload,
  isEditing = false,
  onEditToggle,
  jobs = [],
}) => {
  const [editData, setEditData] = React.useState(candidate);
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [pendingResume, setPendingResume] = React.useState<File | null>(null);
  const [pendingCoverLetter, setPendingCoverLetter] = React.useState<File | null>(null);
  const [uploadingDocuments, setUploadingDocuments] = React.useState(false);

  // Open file in new tab with authentication
  const openAuthenticatedFile = async (filePath: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/files/${filePath}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch file');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');

      // Clean up after a delay
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (error) {
      console.error('Error opening file:', error);
      setSaveError('Failed to open file');
    }
  };

  // Update editData when candidate prop changes
  React.useEffect(() => {
    setEditData(candidate);
  }, [candidate]);


  // Status options
  const statusOptions = [
    'pending',
    'screening', 
    'interviewing',
    'evaluated',
    'hired',
    'rejected',
    'withdrawn',
  ];

  // Removed unused evaluationStatusOptions

  const applicationSourceOptions = [
    'website',
    'referral',
    'recruiting',
    'linkedin',
    'indeed',
    'other',
  ];

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'screening': return 'info';
      case 'interviewing': return 'info';
      case 'evaluated': return 'success';
      case 'hired': return 'success';
      case 'rejected': return 'error';
      case 'withdrawn': return 'default';
      default: return 'default';
    }
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Format date for input
  const formatDateForInput = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  // Handle save
  const handleSave = async () => {
    if (!onUpdate) return;
    
    setSaving(true);
    setSaveError(null);
    try {
      await onUpdate(editData);
      // Successfully saved - exit edit mode
      onEditToggle?.();
    } catch (error: any) {
      console.error('Failed to update candidate:', error);
      // Stay in edit mode on error so user can try again
      setSaveError(error.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    setEditData(candidate);
    setSaveError(null);
    setPendingResume(null);
    setPendingCoverLetter(null);
    onEditToggle?.();
  };

  // Handle document upload
  const handleDocumentUpload = async () => {
    if (!onDocumentUpload || (!pendingResume && !pendingCoverLetter)) return;

    setUploadingDocuments(true);
    try {
      await onDocumentUpload({
        resume: pendingResume || undefined,
        coverLetter: pendingCoverLetter || undefined,
      });
      setPendingResume(null);
      setPendingCoverLetter(null);
    } catch (error: any) {
      setSaveError(error.message || 'Failed to upload documents');
    } finally {
      setUploadingDocuments(false);
    }
  };

  // Calculate time since application
  const getTimeSinceApplication = () => {
    if (!candidate.application_date) return '';
    const appDate = new Date(candidate.application_date);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - appDate.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return '1 day ago';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  };

  return (
    <Stack spacing={3}>
      {/* Header with edit controls */}
      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box>
            <Typography variant="h5" gutterBottom>
              {candidate.first_name} {candidate.last_name}
            </Typography>
            {isEditing ? (
              <TextField
                select
                fullWidth
                label="Job"
                value={editData.job_id || ''}
                onChange={(e) => {
                  setEditData({ 
                    ...editData, 
                    job_id: e.target.value || undefined
                  });
                }}
                sx={{ mt: 1 }}
              >
                <MenuItem value="">
                  <em>No job selected</em>
                </MenuItem>
                {jobs.filter(job => job.status !== 'closed' && job.status !== 'cancelled').map((job) => (
                  <MenuItem key={job.id} value={job.id}>
                    {job.title}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <Typography variant="h6" color="text.secondary">
                {candidate.job_title || (candidate.job_id && jobs.find(j => j.id === candidate.job_id)?.title) || 'Position not specified'}
              </Typography>
            )}
          </Box>
          
          {onUpdate && onEditToggle && (
            <Box>
              {isEditing ? (
                <Box>
                  <Stack direction="row" spacing={1}>
                    <Button
                      startIcon={<Save />}
                      variant="contained"
                      onClick={handleSave}
                      disabled={saving}
                      data-testid="save-candidate-button"
                    >
                      Save
                    </Button>
                    <Button
                      startIcon={<Cancel />}
                      variant="outlined"
                      onClick={handleCancel}
                      disabled={saving}
                    >
                      Cancel
                    </Button>
                  </Stack>
                  {saveError && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      {saveError}
                    </Alert>
                  )}
                </Box>
              ) : (
                <IconButton onClick={onEditToggle} data-testid="edit-candidate-button">
                  <Edit />
                </IconButton>
              )}
            </Box>
          )}
        </Box>

        {/* Status and key info */}
        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
          {isEditing && onUpdate ? (
            <TextField
              select
              size="small"
              value={editData.status}
              onChange={(e) => {
                setEditData({ ...editData, status: e.target.value });
              }}
              disabled={saving}
              sx={{ minWidth: 120 }}
            >
              {statusOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  <Chip
                    label={option.charAt(0).toUpperCase() + option.slice(1)}
                    color={getStatusColor(option) as any}
                    size="small"
                    sx={{ cursor: 'pointer' }}
                  />
                </MenuItem>
              ))}
            </TextField>
          ) : (
            <Chip
              label={candidate.status}
              color={getStatusColor(candidate.status) as any}
              sx={{ fontWeight: 'medium' }}
              data-testid="candidate-status"
            />
          )}
          {candidate.evaluation_status && (
            <Chip
              label={`Evaluation: ${candidate.evaluation_status}`}
              variant="outlined"
              size="small"
            />
          )}
          {(candidate.pi_profile_name || candidate.pi_profile_code) && (
            <Chip
              label={`PI Profile: ${candidate.pi_profile_name || candidate.pi_profile_code}`}
              variant="outlined"
              size="small"
              color="primary"
            />
          )}
        </Box>
      </Paper>

      {/* Contact Information */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Contact Information
        </Typography>
        
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 3,
          }}
        >
          <Box>
            {isEditing ? (
              <TextField
                fullWidth
                label="Email"
                value={editData.email || ''}
                onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                InputProps={{
                  startAdornment: <Email sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            ) : (
              <Box display="flex" alignItems="center" gap={1}>
                <Email sx={{ color: 'text.secondary' }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">Email</Typography>
                  {candidate.email ? (
                    <Link href={`mailto:${candidate.email}`}>
                      <Typography variant="body1">{candidate.email}</Typography>
                    </Link>
                  ) : (
                    <Typography variant="body1" color="text.secondary">Not provided</Typography>
                  )}
                </Box>
              </Box>
            )}
          </Box>
          
          <Box>
            {isEditing ? (
              <TextField
                fullWidth
                label="Phone"
                value={editData.phone || ''}
                onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                InputProps={{
                  startAdornment: <Phone sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            ) : (
              <Box display="flex" alignItems="center" gap={1}>
                <Phone sx={{ color: 'text.secondary' }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">Phone</Typography>
                  {candidate.phone ? (
                    <Link href={`tel:${candidate.phone}`}>
                      <Typography variant="body1">{candidate.phone}</Typography>
                    </Link>
                  ) : (
                    <Typography variant="body1" color="text.secondary">Not provided</Typography>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Application Details */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Application Details
        </Typography>
        
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 3,
          }}
        >
          <Box>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <CalendarToday sx={{ color: 'text.secondary' }} />
              <Box>
                <Typography variant="body2" color="text.secondary">Application Date</Typography>
                <Typography variant="body1">
                  {candidate.application_date ? `${formatDate(candidate.application_date)} (${getTimeSinceApplication()})` : 'Not specified'}
                </Typography>
              </Box>
            </Box>
          </Box>
          
          <Box>
            {isEditing ? (
              <TextField
                fullWidth
                select
                label="Application Source"
                value={editData.application_source}
                onChange={(e) => setEditData({ ...editData, application_source: e.target.value })}
                InputProps={{
                  startAdornment: <Source sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              >
                {applicationSourceOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option ? option.charAt(0).toUpperCase() + option.slice(1) : ''}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <Box display="flex" alignItems="center" gap={1}>
                <Source sx={{ color: 'text.secondary' }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">Source</Typography>
                  <Typography variant="body1">
                    {candidate.application_source === 'other' ? 'Imported' : 
                     candidate.application_source ? candidate.application_source.charAt(0).toUpperCase() + candidate.application_source.slice(1) : 'Not specified'}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>


          {/* Interview Date */}
          <Box>
            {isEditing ? (
              <TextField
                fullWidth
                type="date"
                label="Interview Date"
                value={formatDateForInput(editData.interview_date || '')}
                onChange={(e) => setEditData({ ...editData, interview_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            ) : candidate.interview_date ? (
              <Box display="flex" alignItems="center" gap={1}>
                <CalendarToday sx={{ color: 'text.secondary' }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">Interview Scheduled</Typography>
                  <Typography variant="body1">{formatDate(candidate.interview_date)}</Typography>
                </Box>
              </Box>
            ) : null}
          </Box>
        </Box>
      </Paper>

      {/* Documents */}
      {(isEditing || candidate.resume_file_path || candidate.cover_letter_file_path || candidate.pi_assessment_file_path) && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Documents
          </Typography>

          <Stack spacing={2}>
            {/* Resume */}
            {isEditing ? (
              <Box display="flex" alignItems="center" gap={2}>
                <Description sx={{ color: 'text.secondary' }} />
                <Box flex={1}>
                  <Typography variant="body1">Resume</Typography>
                  {pendingResume ? (
                    <Typography variant="body2" color="primary">
                      {pendingResume.name} (pending upload)
                    </Typography>
                  ) : candidate.resume_file_path ? (
                    <Typography variant="body2" color="text.secondary">
                      {candidate.resume_file_path.split('/').pop()}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No file uploaded
                    </Typography>
                  )}
                </Box>
                <Button
                  component="label"
                  variant="outlined"
                  size="small"
                  startIcon={<CloudUpload />}
                >
                  {candidate.resume_file_path || pendingResume ? 'Replace' : 'Upload'}
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) setPendingResume(file);
                    }}
                  />
                </Button>
                {(pendingResume || candidate.resume_file_path) && (
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => setPendingResume(null)}
                    title="Remove pending file"
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
            ) : candidate.resume_file_path ? (
              <Box display="flex" alignItems="center" gap={2}>
                <Description sx={{ color: 'text.secondary' }} />
                <Box flex={1}>
                  <Typography variant="body1">Resume</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {candidate.resume_file_path.split('/').pop()}
                  </Typography>
                </Box>
                <Button
                  startIcon={<Visibility />}
                  variant="outlined"
                  size="small"
                  onClick={() => openAuthenticatedFile(candidate.resume_file_path!)}
                >
                  View
                </Button>
              </Box>
            ) : null}

            {/* Cover Letter */}
            {isEditing ? (
              <Box display="flex" alignItems="center" gap={2}>
                <Description sx={{ color: 'text.secondary' }} />
                <Box flex={1}>
                  <Typography variant="body1">Cover Letter</Typography>
                  {pendingCoverLetter ? (
                    <Typography variant="body2" color="primary">
                      {pendingCoverLetter.name} (pending upload)
                    </Typography>
                  ) : candidate.cover_letter_file_path ? (
                    <Typography variant="body2" color="text.secondary">
                      {candidate.cover_letter_file_path.split('/').pop()}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No file uploaded
                    </Typography>
                  )}
                </Box>
                <Button
                  component="label"
                  variant="outlined"
                  size="small"
                  startIcon={<CloudUpload />}
                >
                  {candidate.cover_letter_file_path || pendingCoverLetter ? 'Replace' : 'Upload'}
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) setPendingCoverLetter(file);
                    }}
                  />
                </Button>
                {(pendingCoverLetter || candidate.cover_letter_file_path) && (
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => setPendingCoverLetter(null)}
                    title="Remove pending file"
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
            ) : candidate.cover_letter_file_path ? (
              <Box display="flex" alignItems="center" gap={2}>
                <Description sx={{ color: 'text.secondary' }} />
                <Box flex={1}>
                  <Typography variant="body1">Cover Letter</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {candidate.cover_letter_file_path.split('/').pop()}
                  </Typography>
                </Box>
                <Button
                  startIcon={<Visibility />}
                  variant="outlined"
                  size="small"
                  onClick={() => openAuthenticatedFile(candidate.cover_letter_file_path!)}
                >
                  View
                </Button>
              </Box>
            ) : null}

            {/* Upload button for pending files */}
            {isEditing && (pendingResume || pendingCoverLetter) && onDocumentUpload && (
              <Button
                variant="contained"
                startIcon={<CloudUpload />}
                onClick={handleDocumentUpload}
                disabled={uploadingDocuments}
              >
                {uploadingDocuments ? 'Uploading...' : 'Upload Documents'}
              </Button>
            )}

            {candidate.pi_assessment_file_path && (
              <Box display="flex" alignItems="center" gap={2}>
                <Download sx={{ color: 'text.secondary' }} />
                <Box flex={1}>
                  <Typography variant="body1">PI Assessment</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {candidate.pi_assessment_file_path.split('/').pop()}
                  </Typography>
                </Box>
                <Button
                  startIcon={<Visibility />}
                  variant="outlined"
                  size="small"
                  onClick={() => openAuthenticatedFile(candidate.pi_assessment_file_path!)}
                >
                  View
                </Button>
              </Box>
            )}
          </Stack>
        </Paper>
      )}

      {/* Cover Letter */}
      {candidate.cover_letter && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Cover Letter
          </Typography>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
            {candidate.cover_letter}
          </Typography>
        </Paper>
      )}

      {/* PI Assessment */}
      {(isEditing || (candidate.pi_factor_a && candidate.pi_factor_b && candidate.pi_factor_c && candidate.pi_factor_d)) && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            PI Assessment
          </Typography>
          
          {isEditing ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
              <TextField
                fullWidth
                label="Factor A (Dominance)"
                type="number"
                inputProps={{ min: 0, max: 100, step: 0.1 }}
                value={editData.pi_factor_a || ''}
                onChange={(e) => setEditData({ ...editData, pi_factor_a: parseFloat(e.target.value) || undefined })}
                helperText="0-100"
                data-testid="pi-factor-a"
              />
              
              <TextField
                fullWidth
                label="Factor B (Extraversion)"
                type="number"
                inputProps={{ min: 0, max: 100, step: 0.1 }}
                value={editData.pi_factor_b || ''}
                onChange={(e) => setEditData({ ...editData, pi_factor_b: parseFloat(e.target.value) || undefined })}
                helperText="0-100"
                data-testid="pi-factor-b"
              />
              
              <TextField
                fullWidth
                label="Factor C (Patience)"
                type="number"
                inputProps={{ min: 0, max: 100, step: 0.1 }}
                value={editData.pi_factor_c || ''}
                onChange={(e) => setEditData({ ...editData, pi_factor_c: parseFloat(e.target.value) || undefined })}
                helperText="0-100"
                data-testid="pi-factor-c"
              />
              
              <TextField
                fullWidth
                label="Factor D (Formality)"
                type="number"
                inputProps={{ min: 0, max: 100, step: 0.1 }}
                value={editData.pi_factor_d || ''}
                onChange={(e) => setEditData({ ...editData, pi_factor_d: parseFloat(e.target.value) || undefined })}
                helperText="0-100"
                data-testid="pi-factor-d"
              />
            </Box>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">Factor A (Dominance)</Typography>
                <Typography variant="body1" data-testid="pi-factor-a">{candidate.pi_factor_a || 'Not set'}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Factor B (Extraversion)</Typography>
                <Typography variant="body1" data-testid="pi-factor-b">{candidate.pi_factor_b || 'Not set'}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Factor C (Patience)</Typography>
                <Typography variant="body1" data-testid="pi-factor-c">{candidate.pi_factor_c || 'Not set'}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Factor D (Formality)</Typography>
                <Typography variant="body1" data-testid="pi-factor-d">{candidate.pi_factor_d || 'Not set'}</Typography>
              </Box>
            </Box>
          )}
        </Paper>
      )}

      {/* PI Sync Status */}
      {candidate.pi_person_id && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            PI Integration Status
          </Typography>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">PI Person ID</Typography>
              <Typography variant="body1">{candidate.pi_person_id}</Typography>
            </Box>
            
            {candidate.pi_last_sync_at && (
              <Box>
                <Typography variant="body2" color="text.secondary">Last Synced</Typography>
                <Typography variant="body1">
                  {formatDate(candidate.pi_last_sync_at)}
                </Typography>
              </Box>
            )}
            
            {candidate.pi_assessment_completed_at && (
              <Box>
                <Typography variant="body2" color="text.secondary">Assessment Completed</Typography>
                <Typography variant="body1">
                  {formatDate(candidate.pi_assessment_completed_at)}
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      )}

      {/* Notes */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Notes
        </Typography>
        
        {isEditing ? (
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Internal Notes"
            value={editData.notes || ''}
            onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
            placeholder="Add notes about this candidate..."
          />
        ) : (
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
            {candidate.notes || 'No notes added yet.'}
          </Typography>
        )}
      </Paper>
    </Stack>
  );
};

export default CandidateInfo;